import { useTranslation } from 'react-i18next';
import type { LiveClockState } from '@/domain/live-clock';
import { translateKickoffLabel } from '@/i18n/kickoff-label';
import { sanitizeOutcomeDisplay } from '@/i18n/outcome-labels';
import { translateLeagueBreadcrumb } from '@/i18n/sports-label';
import { cn } from '@/lib/cn';
import { isFlagBadgeSrc } from '@/shell/sports-menu-utils';
import kitBlueSm from '@/assets/kits/kit-blue-sm.svg';
import kitWhiteSm from '@/assets/kits/kit-white-sm.svg';
import { Badge } from '../common/badge';
import { Icon } from '../common/icon';
import { OddsButton, type OddsFlash, type OddsState } from '../common/odds-button';
import { LiveClockText } from '../live/LiveClockText';

export interface EventOutcome {
  id: string;
  /** Short outcome label / translate key, e.g. `1`, `X`, `Over`. */
  outcome: string;
  odds: string;
  state?: OddsState;
  flash?: OddsFlash;
  /** Line specifier for totals/handicap, e.g. `total=2.5` — appended after translate. */
  specifier?: string;
  /** Market key used for betslip identity (legacy MarketId). */
  marketId?: string;
  /** Display market name, e.g. `1x2`. */
  marketName?: string;
  /** Numeric odd for betslip; omit when locked / unavailable. */
  oddValue?: number;
  /** Optional friendly outcome name for the betslip row. */
  outcomeName?: string;
  /** Raw API outcome code; preserved when `outcome` is replaced with a display label. */
  outcomeCode?: string;
}

export interface EventCompetitor {
  name: string;
  /** Live main score for this side (points for tennis, total for most sports). */
  score?: string;
  /** Dimmed game / period scores shown beside the main score, e.g. `6 4` or `22 30`. */
  periodScores?: string;
  /** Dimmed set / match score when points are shown as the main score. */
  setScore?: string;
  /** True when this side is currently serving (tennis / volleyball). */
  serving?: boolean;
}

export type EventRowIndicator = 'livestream';

export interface EventRowProps {
  /** Breadcrumb of category and tournament, e.g. `Denmark > Superliga`. */
  league: string;
  /** Raw category name for SportsTranslates lookup. */
  category?: string;
  categoryId?: number;
  /** Raw tournament name for SportsTranslates lookup. */
  tournament?: string;
  tournamentId?: number;
  competitors: [EventCompetitor, EventCompetitor];
  /** Market shorthand shown above the odds, e.g. `1x2`. */
  marketCode: string;
  outcomes: EventOutcome[];
  /** Matches the Figma `Type` property. */
  type?: 'prematch' | 'live';
  /** Matches the Figma `State` property; suspended events stop accepting bets. */
  state?: 'default' | 'suspended';
  /** Prematch kick-off label, e.g. `Today, 16:00`. */
  kickoff?: string;
  /** Live match clock, e.g. `67' · 2HT`. */
  clock?: string;
  /** Tickable live clock state from match extended time. */
  liveClockState?: LiveClockState;
  /** WebsiteTranslates lookup for live period codes when ticking. */
  translatePeriod?: (key: string) => string;
  /** Optional tournament crest / category flag shown in the header. */
  crestSrc?: string;
  /** Product indicators rendered in the meta row when data exists. */
  indicators?: EventRowIndicator[];
  /** Count of further markets, rendered as the `+n` affordance. */
  additionalMarkets?: number;
  /** Prematch Bet Builder available (module + event flag already gated by caller). */
  betBuilderAvailable?: boolean;
  favorite?: boolean;
  onToggleFavorite?: () => void;
  onShowStats?: () => void;
  onSelectOutcome?: (outcome: EventOutcome) => void;
  onOpenEvent?: () => void;
  /** Opens the event in Bet Builder mode when the BB CTA is activated. */
  onOpenBetBuilder?: () => void;
  /** Opens live event details with the stream panel (live rows only). */
  onOpenLiveStream?: () => void;
  className?: string;
}

const KIT_SRC = [kitWhiteSm, kitBlueSm] as const;

export function EventRow({
  league,
  category,
  categoryId,
  tournament,
  tournamentId,
  competitors,
  marketCode,
  outcomes,
  type = 'prematch',
  state = 'default',
  kickoff,
  clock,
  liveClockState,
  translatePeriod,
  crestSrc,
  indicators,
  additionalMarkets,
  betBuilderAvailable = false,
  favorite = false,
  onToggleFavorite,
  onShowStats,
  onSelectOutcome,
  onOpenEvent,
  onOpenBetBuilder,
  onOpenLiveStream,
  className,
}: EventRowProps) {
  const { t, i18n } = useTranslation();
  const suspended = state === 'suspended';
  const isLive = type === 'live';
  const showLivestream = indicators?.includes('livestream') ?? false;
  const kickoffLabel = kickoff ? translateKickoffLabel(kickoff, t) : undefined;
  const versusLabel = t('versus', { defaultValue: 'versus' });
  const liveStreamLabel = t('LiveStream', { defaultValue: 'Live stream available' });
  const leagueLabel =
    category || tournament
      ? translateLeagueBreadcrumb(
          i18n,
          t,
          { category, categoryId, tournament, tournamentId },
          ' > ',
        ) || league
      : league;

  return (
    <article
      className={cn(
        'flex flex-col gap-2 rounded-md border border-border-event bg-bg-surface p-2.5',
        className,
      )}
    >
      <header className="flex items-center gap-2">
        <button
          type="button"
          onClick={onOpenEvent}
          className="flex min-w-0 flex-1 items-center gap-1.5 text-left"
        >
          {crestSrc ? (
            <span
              className={cn(
                'flex size-4 shrink-0 items-center justify-center overflow-clip rounded-full',
                !isFlagBadgeSrc(crestSrc) && 'bg-white',
              )}
            >
              <img
                src={crestSrc}
                alt=""
                className={
                  isFlagBadgeSrc(crestSrc)
                    ? 'size-full object-cover'
                    : 'size-3.5 object-contain'
                }
              />
            </span>
          ) : null}
          <span className="truncate text-caption tracking-wide text-text-secondary">{leagueLabel}</span>
        </button>

        {onShowStats ? (
          <IconAction
            icon="stats"
            label={t('MatchStatistics', { defaultValue: 'Match statistics' })}
            onClick={onShowStats}
          />
        ) : null}
        <IconAction
          icon="star"
          label={
            favorite
              ? t('RemoveFromFavourites', { defaultValue: 'Remove from favourites' })
              : t('AddToFavourites', { defaultValue: 'Add to favourites' })
          }
          pressed={favorite}
          onClick={onToggleFavorite}
        />
      </header>

      <div className="flex items-center gap-2">
        {isLive ? (
          <div className="flex items-center gap-2">
            <Badge tone="live">{t('Live', { defaultValue: 'Live' })}</Badge>
            {clock || liveClockState ? (
              <LiveClockText
                liveClockState={liveClockState}
                clock={clock}
                translatePeriod={translatePeriod}
                className="text-body-s text-text-secondary"
              />
            ) : null}
          </div>
        ) : kickoffLabel ? (
          <span className="text-body-s tracking-wide text-text-secondary">{kickoffLabel}</span>
        ) : null}
        {showLivestream || betBuilderAvailable ? (
          <span className="flex h-4 items-center gap-1.5 text-text-primary">
            {showLivestream ? (
              isLive && onOpenLiveStream ? (
                <button
                  type="button"
                  onClick={onOpenLiveStream}
                  className="inline-flex h-4 items-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
                  aria-label={liveStreamLabel}
                >
                  <Icon name="monitor-play" size={16} />
                </button>
              ) : (
                <Icon name="monitor-play" size={16} label={liveStreamLabel} />
              )
            ) : null}
            {betBuilderAvailable ? (
              <button
                type="button"
                onClick={onOpenBetBuilder ?? onOpenEvent}
                className="inline-flex h-4 items-center rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
                aria-label={t('BetBuilder', { defaultValue: 'Bet Builder' })}
              >
                <Icon name="betbuilder" size={16} />
              </button>
            ) : null}
          </span>
        ) : null}
      </div>

      <button
        type="button"
        onClick={onOpenEvent}
        className="flex flex-col gap-2 text-left"
        aria-label={`${competitors[0].name} ${versusLabel} ${competitors[1].name}`}
      >
        {competitors.map((competitor, index) => (
          <span key={`${competitor.name}-${index}`} className="flex items-center gap-2">
            <span className="relative size-6 shrink-0 overflow-clip">
              <img src={KIT_SRC[index]} alt="" className="size-full object-contain" />
            </span>
            <span className="min-w-0 flex-1 truncate text-body-m font-bold text-text-primary">
              {competitor.name}
            </span>
            {competitor.serving ? (
              <span
                className="size-1.5 shrink-0 rounded-full bg-status-warning"
                title={t('Serving', { defaultValue: 'Serving' })}
                aria-label={t('Serving', { defaultValue: 'Serving' })}
              />
            ) : null}
            {competitor.setScore !== undefined ? (
              <span className="text-body-s tabular-nums text-text-secondary/70">
                {competitor.setScore}
              </span>
            ) : null}
            {competitor.periodScores ? (
              <span className="text-body-s tabular-nums text-text-secondary">
                {competitor.periodScores}
              </span>
            ) : null}
            {competitor.score !== undefined ? (
              <span className="text-body-l font-bold tabular-nums text-text-primary">
                {competitor.score}
              </span>
            ) : null}
          </span>
        ))}
      </button>

      <p className="text-caption text-text-secondary">{marketCode}</p>

      <div className="flex items-stretch gap-1">
        {outcomes.map((outcome) => (
          <OddsButton
            key={outcome.id}
            outcome={sanitizeOutcomeDisplay(outcome.outcome)}
            odds={outcome.odds}
            state={suspended ? 'locked' : (outcome.state ?? 'default')}
            flash={outcome.flash ?? 'none'}
            onClick={() => onSelectOutcome?.(outcome)}
          />
        ))}
        {additionalMarkets !== undefined && additionalMarkets > 0 ? (
          <button
            type="button"
            onClick={onOpenEvent}
            className={cn(
              'flex min-h-9 shrink-0 items-center justify-center rounded-sm bg-odds-default',
              'px-2.5 text-body-s text-text-secondary hover:bg-bg-elevated',
            )}
          >
            +{additionalMarkets}
            <span className="sr-only">
              {' '}
              {t('MoreMarkets', { defaultValue: 'more markets' })}
            </span>
          </button>
        ) : null}
      </div>
    </article>
  );
}

interface IconActionProps {
  icon: Parameters<typeof Icon>[0]['name'];
  label: string;
  pressed?: boolean;
  onClick?: () => void;
}

/** The 32px square icon buttons in the Figma EventRow header. */
function IconAction({ icon, label, pressed, onClick }: IconActionProps) {
  const favoritedStar = icon === 'star' && pressed;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-pressed={pressed}
      className={cn(
        'flex size-8 shrink-0 items-center justify-center rounded-md border border-border-default',
        'bg-bg-surface text-icon-muted hover:bg-bg-elevated hover:text-icon-default',
        favoritedStar ? 'text-action-primary hover:text-action-primary' : pressed && 'text-icon-default',
      )}
    >
      <Icon
        name={icon}
        size={14}
        {...(favoritedStar ? { fill: 'currentColor', stroke: 'none' } : {})}
      />
    </button>
  );
}
